import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  FaCamera,
  FaImage,
  FaSearchMinus,
  FaSearchPlus,
  FaSpinner,
  FaTimes,
  FaTrashAlt,
  FaUpload,
} from 'react-icons/fa';
import {
  AVATAR_ACCEPT,
  AVATAR_MAX_ZOOM,
  AVATAR_MIN_ZOOM,
  clampPan,
  cropFrame,
  exportAvatarBlob,
  getAvatarErrorCode,
  loadImageFile,
} from '../../utils/avatarClient';
import './AvatarEditor.css';

function getInitials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return String(name || '?').slice(0, 2).toUpperCase();
}

export default function AvatarEditor({
  open,
  currentUrl,
  displayName,
  busy = false,
  onClose,
  onSave,
  onRemove,
}) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const dragRef = useRef(null);
  const previewUrlRef = useRef(null);
  const [image, setImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState('');
  const [dropActive, setDropActive] = useState(false);

  const resetDraft = useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setImage(null);
    setPreviewUrl('');
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setError('');
    setDropActive(false);
    setDragging(false);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    resetDraft();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event) => {
      if (event.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, busy, onClose, resetDraft]);

  const applyFile = useCallback(async (file) => {
    const code = getAvatarErrorCode(file);
    if (code) {
      setError(t(`avatar.errors.${code}`));
      return;
    }
    try {
      const next = await loadImageFile(file);
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = next.url;
      setImage(next.image);
      setPreviewUrl(next.url);
      setZoom(1);
      setPan({ x: 0, y: 0 });
      setError('');
    } catch (err) {
      setError(t(`avatar.errors.${err.message === 'small' ? 'small' : 'invalid'}`));
    }
  }, [t]);

  const onPointerDown = (event) => {
    if (!image || busy) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = { x: event.clientX, y: event.clientY, pan };
    setDragging(true);
  };

  const onPointerMove = (event) => {
    if (!dragRef.current || !image) return;
    const next = clampPan(
      image,
      zoom,
      dragRef.current.pan.x + (event.clientX - dragRef.current.x),
      dragRef.current.pan.y + (event.clientY - dragRef.current.y)
    );
    setPan(next);
  };

  const stopDrag = () => {
    dragRef.current = null;
    setDragging(false);
  };

  const onWheel = (event) => {
    if (!image || busy) return;
    event.preventDefault();
    const delta = event.deltaY > 0 ? -0.08 : 0.08;
    setZoom((current) => {
      const nextZoom = Math.min(AVATAR_MAX_ZOOM, Math.max(AVATAR_MIN_ZOOM, current + delta));
      setPan((currentPan) => clampPan(image, nextZoom, currentPan.x, currentPan.y));
      return nextZoom;
    });
  };

  const handleSave = async () => {
    if (!image || busy) return;
    try {
      const blob = await exportAvatarBlob(image, zoom, pan.x, pan.y);
      const file = new File([blob], 'avatar.jpg', { type: 'image/jpeg' });
      await onSave(file);
    } catch {
      setError(t('avatar.errors.export'));
    }
  };

  const frame = image ? cropFrame(image, zoom, pan.x, pan.y) : null;

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="avatar-modal-backdrop"
          role="presentation"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => {
            if (!busy) onClose();
          }}
        >
          <motion.div
            className="avatar-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="avatar-modal-title"
            initial={{ opacity: 0, y: 18, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            onClick={(event) => event.stopPropagation()}
          >
            <header className="avatar-modal-head">
              <div>
                <p className="avatar-modal-kicker">{t('avatar.kicker')}</p>
                <h3 id="avatar-modal-title">{t('avatar.title')}</h3>
                <p>{t('avatar.lead')}</p>
              </div>
              <button
                type="button"
                className="avatar-icon-btn"
                onClick={onClose}
                disabled={busy}
                aria-label={t('avatar.close')}
              >
                <FaTimes />
              </button>
            </header>

            <div className="avatar-modal-body">
              <div
                className={`avatar-stage${image ? ' is-crop' : ''}${dropActive ? ' is-drop' : ''}${dragging ? ' is-dragging' : ''}`}
                onDragEnter={(event) => {
                  event.preventDefault();
                  setDropActive(true);
                }}
                onDragOver={(event) => event.preventDefault()}
                onDragLeave={() => setDropActive(false)}
                onDrop={(event) => {
                  event.preventDefault();
                  setDropActive(false);
                  applyFile(event.dataTransfer.files?.[0]);
                }}
                onPointerDown={image ? onPointerDown : undefined}
                onPointerMove={image ? onPointerMove : undefined}
                onPointerUp={image ? stopDrag : undefined}
                onPointerCancel={image ? stopDrag : undefined}
                onWheel={image ? onWheel : undefined}
              >
                {image && frame ? (
                  <img
                    src={previewUrl}
                    alt=""
                    draggable={false}
                    style={{
                      width: frame.displayW,
                      height: frame.displayH,
                      transform: `translate(${frame.left}px, ${frame.top}px)`,
                    }}
                  />
                ) : currentUrl ? (
                  <img src={currentUrl} alt="" />
                ) : (
                  <span>{getInitials(displayName)}</span>
                )}
                {!image && (
                  <div className="avatar-stage-hint">
                    <FaImage aria-hidden="true" />
                    <strong>{t('avatar.dropTitle')}</strong>
                    <p>{t('avatar.dropText')}</p>
                  </div>
                )}
              </div>

              {image ? (
                <div className="avatar-zoom">
                  <FaSearchMinus aria-hidden="true" />
                  <input
                    type="range"
                    min={AVATAR_MIN_ZOOM}
                    max={AVATAR_MAX_ZOOM}
                    step="0.01"
                    value={zoom}
                    disabled={busy}
                    aria-label={t('avatar.zoom')}
                    onChange={(event) => {
                      const nextZoom = Number(event.target.value);
                      setZoom(nextZoom);
                      setPan((current) => clampPan(image, nextZoom, current.x, current.y));
                    }}
                  />
                  <FaSearchPlus aria-hidden="true" />
                </div>
              ) : (
                <p className="avatar-hint">{t('avatar.formats')}</p>
              )}

              {error ? <p className="avatar-error">{error}</p> : null}

              <div className="avatar-file-row">
                <input
                  ref={inputRef}
                  type="file"
                  accept={AVATAR_ACCEPT}
                  hidden
                  onChange={(event) => {
                    applyFile(event.target.files?.[0]);
                    event.target.value = '';
                  }}
                />
                <button
                  type="button"
                  className="avatar-btn is-ghost"
                  disabled={busy}
                  onClick={() => inputRef.current?.click()}
                >
                  <FaUpload aria-hidden="true" />
                  {image ? t('avatar.replace') : t('avatar.choose')}
                </button>
                {image && (
                  <button
                    type="button"
                    className="avatar-btn is-ghost"
                    disabled={busy}
                    onClick={resetDraft}
                  >
                    {t('avatar.clear')}
                  </button>
                )}
              </div>
            </div>

            <footer className="avatar-modal-footer">
              <div>
                {currentUrl ? (
                  <button
                    type="button"
                    className="avatar-btn is-danger"
                    disabled={busy}
                    onClick={onRemove}
                  >
                    <FaTrashAlt aria-hidden="true" />
                    {t('avatar.remove')}
                  </button>
                ) : null}
              </div>
              <div className="avatar-modal-actions">
                <button type="button" className="avatar-btn is-ghost" disabled={busy} onClick={onClose}>
                  {t('avatar.cancel')}
                </button>
                <button
                  type="button"
                  className="avatar-btn"
                  disabled={busy || !image}
                  onClick={handleSave}
                >
                  {busy ? <FaSpinner className="social-spin" /> : <FaCamera aria-hidden="true" />}
                  {busy ? t('avatar.saving') : t('avatar.save')}
                </button>
              </div>
            </footer>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}

export function AvatarEditTrigger({
  currentUrl,
  displayName,
  onClick,
  className = '',
  frameClassName = 'social-avatar',
}) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      className={`avatar-edit-trigger ${className}`.trim()}
      onClick={onClick}
      aria-label={t('avatar.changeAria')}
    >
      <span className={frameClassName} aria-hidden="true">
        {currentUrl ? <img src={currentUrl} alt="" /> : <span>{getInitials(displayName)}</span>}
      </span>
      <span className="avatar-edit-badge" aria-hidden="true">
        <FaCamera />
      </span>
    </button>
  );
}
